/**
 * Agent Context
 * React context for managing agent authentication state
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    getAgentProfile,
    isAgentAuthenticated as checkAuth,
    logoutAgent as doLogout,
    setAgentToken
} from '../services/agentService';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AgentContext = createContext(null);

export const useAgent = () => {
    const context = useContext(AgentContext);
    if (!context) {
        throw new Error('useAgent must be used within an AgentProvider');
    }
    return context;
};

export const AgentProvider = ({ children }) => {
    const [agentProfile, setAgentProfile] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check authentication on mount and on state change
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                console.log("Firebase user detected, fetching agent profile...");
                await checkAuthStatus();
            } else {
                console.log("No Firebase user, clearing agent state");
                setIsAuthenticated(false);
                setAgentProfile(null);
                setAgentToken(null);
                setIsLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const checkAuthStatus = async () => {
        setIsLoading(true);
        try {
            if (checkAuth()) {
                const profile = await getAgentProfile();
                setAgentProfile(profile);
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                setAgentProfile(null);
            }
        } catch (err) {
            console.error('Auth check failed:', err);
            setIsAuthenticated(false);
            setAgentProfile(null);
            // Clear invalid token
            setAgentToken(null);
        } finally {
            setIsLoading(false);
        }
    };

    const login = useCallback(async (token) => {
        setIsLoading(true);
        setError(null);
        try {
            setAgentToken(token);
            const profile = await getAgentProfile();
            setAgentProfile(profile);
            setIsAuthenticated(true);
            return true;
        } catch (err) {
            console.error('Login failed:', err);
            setError(err.message || 'Login failed');
            setAgentToken(null);
            setIsAuthenticated(false);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        doLogout();
        setAgentProfile(null);
        setIsAuthenticated(false);
        setError(null);
    }, []);

    const refreshProfile = useCallback(async () => {
        if (!isAuthenticated) return null;
        try {
            const profile = await getAgentProfile();
            setAgentProfile(profile);
            return profile;
        } catch (err) {
            console.error('Profile refresh failed:', err);
            return null;
        }
    }, [isAuthenticated]);

    const value = {
        agentProfile,
        isAuthenticated,
        isLoading,
        error,
        login,
        logout,
        refreshProfile,
        checkAuthStatus
    };

    return (
        <AgentContext.Provider value={value}>
            {children}
        </AgentContext.Provider>
    );
};

export default AgentContext;
